import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Briefcase, Plus, Pencil, Trash2, EyeOff, Building2, CalendarRange } from 'lucide-react';
import { toast } from '../../lib/toast';
import { API, auth, inputCls, Field, Modal, PrimaryBtn, errMsg, fmtDate } from '../organization/finance/shared';

const EMPTY = { organization_name: '', title: '', sport: 'football', category: '', start_date: '', end_date: '', description: '', show_in_public: true };

const monthsBetween = (start, end) => {
  if (!start) return 0;
  const a = new Date(start); const b = end ? new Date(end) : new Date();
  return Math.max(0, (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()));
};
const monthsLabel = (m) => {
  if (!m) return 'أقل من شهر';
  const y = Math.floor(m / 12); const r = m % 12;
  return [y ? `${y} سنة` : null, r ? `${r} شهر` : null].filter(Boolean).join(' و');
};
const durationLabel = (start, end) => monthsLabel(monthsBetween(start, end));

export default function CoachExperiencesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/coaches/profile/me/experiences`, auth()); setItems(res.data); } catch { setItems([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const sorted = useMemo(() => [...items].sort((a, b) => String(b.start_date || '').localeCompare(String(a.start_date || ''))), [items]);
  const totalMonths = useMemo(() => items.reduce((s, x) => s + monthsBetween(x.start_date, x.end_date), 0), [items]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setModal(true); };
  const openEdit = (x) => { setEditing(x); setForm(x); setModal(true); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, end_date: form.end_date || null };
    try {
      if (editing) await axios.put(`${API}/coaches/profile/me/experiences/${editing.id}`, payload, auth());
      else await axios.post(`${API}/coaches/profile/me/experiences`, payload, auth());
      toast.success('تم الحفظ');
      setModal(false); load();
    } catch (err) { toast.error(errMsg(err, 'فشل الحفظ')); }
  };

  const remove = async (id) => {
    try { await axios.delete(`${API}/coaches/profile/me/experiences/${id}`, auth()); toast.success('تم الحذف'); setConfirmId(null); load(); }
    catch (err) { toast.error(errMsg(err, 'فشل الحذف')); }
  };

  return (
    <div className="space-y-4" data-testid="coach-experiences-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 text-white shadow-md shadow-teal-500/20"><Briefcase size={19} strokeWidth={2.2} /></span>
          <div>
            <h3 className="text-base font-bold text-slate-800">المسيرة التدريبية</h3>
            <p className="mt-0.5 text-xs text-slate-500" data-testid="coach-exp-stats">
              {items.length} خبرة{totalMonths ? ` · إجمالي ${monthsLabel(totalMonths)}` : ''}
            </p>
          </div>
        </div>
        <PrimaryBtn onClick={openNew} data-testid="coach-exp-add-btn"><Plus size={16} /> إضافة خبرة</PrimaryBtn>
      </div>

      {loading ? (
        <div className="space-y-2">{[0, 1].map((i) => <div key={i} className="h-24 animate-pulse rounded-2xl bg-slate-100" />)}</div>
      ) : sorted.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 px-6 py-12 text-center" data-testid="coach-exp-empty">
          <span className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50 text-teal-600"><Briefcase size={22} /></span>
          <p className="text-sm font-bold text-slate-700">لا توجد خبرات مضافة بعد</p>
          <p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-slate-500">أضف الأندية والأكاديميات التي دربت فيها بالتواريخ — هذا أهم ما تنظر إليه المنشآت في ملفك.</p>
          <button onClick={openNew} className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#16233b] px-5 py-2.5 text-sm font-bold text-white transition-all hover:brightness-110 active:scale-95" data-testid="coach-exp-empty-add-btn"><Plus size={15} /> إضافة أول خبرة</button>
        </div>
      ) : (
        <div className="relative ps-5" data-testid="coach-exp-timeline">
          <span className="absolute inset-y-2 start-[7px] w-px bg-gradient-to-b from-teal-300 via-slate-200 to-transparent" />
          <div className="space-y-3">
            {sorted.map((x) => {
              const current = !x.end_date;
              return (
                <div key={x.id} className="relative">
                  <span className={`absolute -start-[21px] top-5 h-3.5 w-3.5 rounded-full border-2 border-white ${current ? 'bg-teal-500 ring-2 ring-teal-200' : 'bg-slate-300'}`} />
                  <div className="group rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-all hover:-translate-y-0.5 hover:border-teal-200 hover:shadow-[0_8px_24px_rgba(15,23,42,0.07)]" data-testid="coach-exp-row">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="text-sm font-bold text-slate-800">{x.title}</h4>
                          {current && <span className="rounded-full bg-teal-50 px-2 py-0.5 text-[11px] font-bold text-teal-700 ring-1 ring-teal-200">مستمرة</span>}
                          {!x.show_in_public && <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-500"><EyeOff size={11} /> مخفية</span>}
                        </div>
                        <p className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600"><Building2 size={12} className="text-slate-400" /> {x.organization_name}</p>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                          <span className="inline-flex items-center gap-1 rounded-md bg-slate-50 px-2 py-1">
                            <CalendarRange size={11} /> {fmtDate(x.start_date)} — {x.end_date ? fmtDate(x.end_date) : 'حتى الآن'}
                          </span>
                          <span className="rounded-md bg-slate-50 px-2 py-1 font-semibold">{durationLabel(x.start_date, x.end_date)}</span>
                          {x.category && <span className="rounded-md bg-slate-50 px-2 py-1">{x.category}</span>}
                          {x.sport && <span className="rounded-md bg-slate-50 px-2 py-1">{x.sport}</span>}
                        </div>
                        {x.description && <p className="mt-2.5 border-t border-slate-100 pt-2.5 text-xs leading-relaxed text-slate-600">{x.description}</p>}
                      </div>
                      <div className="flex shrink-0 gap-1.5">
                        <button onClick={() => openEdit(x)} className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 transition-colors hover:border-teal-300 hover:text-teal-600" data-testid="coach-exp-edit-btn"><Pencil size={14} /></button>
                        <button onClick={() => setConfirmId(x.id)} className="rounded-lg border border-slate-200 bg-white p-2 text-red-500 transition-colors hover:border-red-300" data-testid="coach-exp-delete-btn"><Trash2 size={14} /></button>
                      </div>
                    </div>
                    {confirmId === x.id && (
                      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-red-50 p-2.5 ps-3" data-testid="coach-exp-delete-confirm">
                        <span className="text-xs font-semibold text-red-700">تأكيد حذف هذه الخبرة؟</span>
                        <div className="flex gap-2">
                          <button onClick={() => setConfirmId(null)} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-600">إلغاء</button>
                          <button onClick={() => remove(x.id)} className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-bold text-white" data-testid="coach-exp-delete-confirm-btn">حذف</button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Modal open={modal} title={editing ? 'تعديل خبرة' : 'إضافة خبرة سابقة'} onClose={() => setModal(false)} wide>
        <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="اسم المنشأة" required><input required className={inputCls} value={form.organization_name} onChange={(e) => setForm({ ...form, organization_name: e.target.value })} data-testid="coach-exp-input-org" /></Field>
          <Field label="المسمى الوظيفي" required><input required className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="coach-exp-input-title" /></Field>
          <Field label="اللعبة"><input className={inputCls} value={form.sport || ''} onChange={(e) => setForm({ ...form, sport: e.target.value })} /></Field>
          <Field label="الفئة"><input className={inputCls} value={form.category || ''} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="تحت 17" /></Field>
          <Field label="تاريخ البداية" required><input required type="date" className={inputCls} value={form.start_date || ''} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="coach-exp-input-start" /></Field>
          <Field label="تاريخ النهاية (اتركها فارغة إن كانت مستمرة)"><input type="date" className={inputCls} value={form.end_date || ''} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>
          <div className="col-span-full"><Field label="وصف مختصر (إنجازاتك وأهم نتائجك)"><textarea rows={3} className={inputCls} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="مثال: تأهل الفريق لنهائي دوري الفئات السنية، تطوير 6 لاعبين للفريق الأول" /></Field></div>
          <label className="col-span-full flex w-fit cursor-pointer items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:border-teal-300">
            <input type="checkbox" className="h-4 w-4 accent-teal-600" checked={Boolean(form.show_in_public)} onChange={(e) => setForm({ ...form, show_in_public: e.target.checked })} /> إظهار هذه الخبرة في الملف العام
          </label>
          <div className="col-span-full flex justify-end gap-2 border-t border-slate-100 pt-3">
            <button type="button" onClick={() => setModal(false)} className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-bold text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="coach-exp-submit-btn">حفظ</PrimaryBtn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
